import * as Yup from 'yup'
import React from 'react'
import { FilePreview, FormItemProps } from '@/components/Form/types'
import SectionTargetObject from '../components/SelectTargetObject'
import SectionFramePlacement from '../components/SectionFramePlacement'
import ImageComponent from '@/components/UiElements/ImageComponent'
import { getSectionAssignmentItems } from './sectionAssignmentItems'
import MaterialPicker from '@/components/ProductProfile/components/MaterialPicker'
import TakeSnapshot from '@/components/ProductProfile/components/TakeSnapshot'
import EngravingSetting from '@/components/ProductProfile/components/EngravingSetting'
import store from '@/store'





// Colour profiles are inkjet: the shopper picks colours per element in the
// customizer and uploads print as-is, so there is no engraving substrate for the
// admin to choose. Unknown defaults to laser — nothing should vanish from an
// older profile that predates the setting.
const isLaserProfile = (profileType?: string) => profileType !== 'color'

/**
 * Which section model this profile runs on, as the form sees it.
 *
 * Resolved once on load into threeViewer.sectionModel (see Preview3dPlugin) through the
 * shared resolver, so the form, the renderer and the Customizer all answer the same way.
 * Cylindrical profiles — every one that exists today — see exactly the fields they always
 * have.
 */
const isFrameModel = () => (window as any).threeViewer?.sectionModel === 'frame'

interface SectionFormItemsOptions {
	profileType?: string
}

const createSectionFormItems = ({ profileType }: SectionFormItemsOptions = {}): Array<FormItemProps> => [

	{
		name: 'sectionId',
		type: 'Hidden',
	},
	{
		name: 'name',
		label: 'Name',
		help: 'Enter a name that identifies this customizable section, such as Front, Back, or Handle.',
		placeholder: 'Enter section name',
		type: 'Text',
		defaultValue: null,
		validation: Yup.string()
			.required('Required')
			.test('name-format', 'Only letters, numbers, and underscores are allowed', function (value) {
				if (store.companySetting?.machine === 'neatolabs') {
					return /^[a-zA-Z0-9_]+$/.test(value || '')
				}
				return true
			}),
		classNames: { container: 'col-span-12 ' },
		autoFocus: true,
	},
	{
		name: 'targetObject',
		label: 'Target Object',
		type: 'Preview',
		itemOptions: {
			preview: SectionTargetObject,
		},
		defaultValue: null,
		validation: Yup.object().required('Required').typeError('Required'),
		classNames: { container: 'col-span-12 ' },
	},
	{
		name: 'placement',
		label: 'Placement',
		help: 'Where the section sits on the model and which way it faces. Click the model to place it.',
		type: 'Preview',
		itemOptions: {
			preview: SectionFramePlacement,
		},
		defaultValue: null,
		validation: Yup.object().nullable(),
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => Boolean(data?.targetObject) && isFrameModel(),
	},
	{
		name: 'width',
		label: 'Width (mm)',
		help: 'Set the physical width of the customizable area in millimeters.',
		placeholder: 'mm',
		type: 'Range',
		itemOptions: {
			min: 0,
			step: 0.1,
			max: 100,
		},
		defaultValue: null,
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			return data?.targetObject
		},
	},
	{
		name: 'height',
		label: 'Height (mm)',
		help: 'Set the physical height of the customizable area in millimeters.',
		placeholder: 'mm',
		type: 'Range',
		itemOptions: {
			min: 0,
			step: 0.1,
			max: 100,
		},
		defaultValue: null,
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			return data?.targetObject
		},
	},
	{
		name: 'face',
		label: 'Face',
		help: 'Choose whether this section is positioned on the front or back face of the selected object.',
		type: 'Select',
		itemOptions: {
			list: [
				{ value: 'front', label: 'Front' },
				{ value: 'back', label: 'Back' },
			],
		},
		defaultValue: { value: 'front', label: 'Front' },
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			if (!data?.targetObject || isFrameModel()) return false
			const proj = (window as any).threeViewer?.uvProjection
			return proj === 'planar' || proj === 'box'
		},
	},
	{
		name: 'radialAngle',
		label: 'Radial Angle (degrees)',
		help: 'Rotate the section around the selected 3D object to place it on the correct side of a curved surface.',
		placeholder: 'Degrees',
		type: 'Range',
		itemOptions: {
			min: 0,
			step: 1,
			max: 360,
		},
		defaultValue: null,
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			if (!data?.targetObject || isFrameModel()) return false
			const proj = (window as any).threeViewer?.uvProjection
			return proj !== 'planar' && proj !== 'box'
		},
	},
	{
		// Hidden for now — always saved as 0 (see mapSectionToSave)
		name: 'centerRadialAngle',
		type: 'Hidden',
		defaultValue: '0',
	},
	{
		name: 'offsetX',
		label: 'Offset X (mm)',
		help: 'Move the section horizontally from the center of the selected object. Negative and positive values move it in opposite directions.',
		placeholder: 'mm',
		type: 'Range',
		itemOptions: {
			min: -200,
			step: 0.1,
			max: 200,
		},
		defaultValue: 0,
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			if (!data?.targetObject || isFrameModel()) return false
			const proj = (window as any).threeViewer?.uvProjection
			return proj === 'planar' || proj === 'box'
		},
	},
	{
		name: 'offsetY',
		label: 'Offset From Bottom (mm)',
		help: 'Set the vertical position of the section by measuring upward from the bottom of the selected object.',
		placeholder: 'mm',
		type: 'Range',
		itemOptions: {
			min: 0,
			step: 0.1,
			max: 100,
		},
		defaultValue: null,
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			// A frame carries its own origin on the surface; there is no 'bottom' to
			// measure up from once the section is no longer on an unrolled cylinder.
			return Boolean(data?.targetObject) && !isFrameModel()
		},
	},
	{
		name: 'thumbnail',
		label: 'Thumbnail Image',
		type: 'Preview',
		itemOptions: {
			preview: (props: any) => (
				<TakeSnapshot onChange={props?.onChange} formData={props?.formData} updateForm={props?.updateForm} />
			),
		},
		defaultValue: null,
		validation: Yup.object().required('Required').typeError('Required'),
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			return data?.targetObject
		},
	},
	{
		name: 'mask',
		type: 'File',
		label: 'Section Mask Image (Optional)',
		hint: 'Usually used to mask out product logo areas or handles.',
		help: 'Upload an optional image mask to exclude parts of the product from the customizable section.',
		classNames: {
			container: 'col-span-12 break-line',
		},
		placeholder: 'Drag & Drop Mask image here',
		itemOptions: {
			filePreview: (file: FilePreview) => (
				file.string ? <ImageComponent
					src={file.string}
					style={{ height: '70px', width: '70px' }}
					className="w-[70px] h-[70px]"
					id={file.uuid}
				/> : null
			),
			acceptFormat: 'image/*, .avif, .webp, .svg, .png, .jpg, .jpeg',
		},
		condition: (data: any) => {
			return data?.targetObject
		},


	},
	{
		name: 'materialType',
		label: 'Material Type',
		help: 'Choose the material effect used to represent engraved artwork in the 3D preview.',
		placeholder: 'Select material type',
		type: 'Select',
		// Steel, matching how every existing 3D profile already renders. Do not
		// change this default without checking the customizer's own fallback in
		// applyDesignToTargetObjectMaterial.js — profiles saved before this field
		// existed carry no material_type and resolve there instead.
		defaultValue: { value: 'steel', label: 'Steel / Metal' },
		itemOptions: {
			list: [
				{ value: 'custom', label: 'Custom (upload pattern)' },
				{ value: 'steel', label: 'Steel / Metal' },
				{ value: 'gold', label: 'Gold' },
				{ value: 'sandblast', label: 'Sandblast / Frosted' },
				{ value: 'anodized', label: 'Anodized' },
				{ value: 'wood_burn', label: 'Wood Burn' },
				{ value: 'glass_etch', label: 'Glass Etch' },
				{ value: 'color', label: 'Direct Color' },
			],
		},
		classNames: { container: 'col-span-12 break-line' },
		condition: () => isLaserProfile(profileType),
	},
	{
		name: 'materialSource',
		label: 'Custom Material',
		help: 'Choose a solid colour or upload a pattern image for the engraved artwork. A custom material uses one or the other, not both.',
		type: 'Preview',
		defaultValue: null,
		itemOptions: {
			preview: (props: any) => (
				<MaterialPicker formData={props?.formData} onChange={props?.onChange} />
			),
		},
		validation: Yup.mixed().test('material-source-required', 'Required', function (value) {
			// Anything that is not a populated object means this section has no custom
			// material to check — a colour profile (where the field is hidden), or a
			// laser section on a built-in material. Note the form coerces a missing
			// value to '' rather than null, so a null check alone would reject those.
			if (!value || typeof value !== 'object') return true
			// Siblings are not visible here (each item validates alone), so this only
			// checks shape. mapSectionToSave enforces that a custom material really
			// has a colour or a pattern, where the whole section is in scope.
			const source = value as { mode?: string; color?: string; file?: unknown }
			return source.mode === 'pattern' ? source.file != null : !!source.color
		}),
		classNames: { container: 'col-span-12' },
		condition: (data: any) => isLaserProfile(profileType)
			&& (data?.materialType?.value || data?.materialType) === 'custom',
	},
	...getSectionAssignmentItems({ condition: (data: any) => data?.targetObject }),

	{
		name: 'engravingSetting',
		label: 'Engraving Setting',
		type: 'Preview',
		itemOptions: {
			preview: (props: any) => (
				<EngravingSetting onChange={props?.onChange} formData={props?.formData} updateForm={props?.updateForm} />
			),
		},
		defaultValue: null,
		validation: Yup.mixed().test('engraving-required', 'Required', function (value) {
			if (store.companySetting?.machine === 'neatolabs') {
				return value != null && typeof value === 'object'
			}
			return true
		}),
		classNames: { container: 'col-span-12 ' },
		condition: (data: any) => {
			return data?.targetObject && store.companySetting.machine === 'neatolabs'
		},
	},


	{
		name: 'sectionAngle',
		type: 'Hidden',
	},
	{
		name: 'divider',
		type: 'Divider',
		classNames: { container: 'col-span-12' },
	},



]

export default createSectionFormItems
